import { bunja } from 'bunja';
import { atom } from 'jotai';

import { weakR230522SocketBunja } from '@flipster/r230522/original/socket';
import { SpotOrder, spotOrdersBunja } from '@flipster/r230522/original/spot-order';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '../../../symbol';
import { OrderEventContext, OrderEventErrorInfo, OrderEventTracker } from '../../tracker';
import { spotFormBunja } from './form';

export const spotEventBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol } = bunja.use(tradeSymbolBunja);
  const { availableUsdtAtom, availableBaseCurrencyAtom, sideAtom, priceTypeAtom, quantityAtom, amountAtom } =
    bunja.use(spotFormBunja);

  bunja.use(subscribeOrderEventBunja);

  const getTrackOrderClickParamsAtom = atom((get) => {
    const availableUsdt = get(availableUsdtAtom).toString();
    const availableBaseCurrency = get(availableBaseCurrencyAtom);
    const sideType = get(sideAtom);
    const priceType = get(priceTypeAtom);
    const quantity = get(quantityAtom);
    const amount = get(amountAtom);

    const tradeType = 'spot' as const;
    const side: 'buy' | 'sell' = sideType === 'Buy' ? 'buy' : 'sell';

    return {
      trade_type: tradeType,
      symbol,
      order_type: priceType,
      side,
      amount: quantity ? Number(quantity) : null,
      amount_usdt: amount ? Number(amount) : 0,
      available_funds: availableUsdt,
      available_pair: Number(availableBaseCurrency),
    };
  });

  function trackOrderClick() {
    segment.track(TradeEvent.OrderClick, store.get(getTrackOrderClickParamsAtom));
  }

  const orderEventContextInfoAtom = atom((get) => {
    const sideType = get(sideAtom);
    const priceType = get(priceTypeAtom);
    const quantity = get(quantityAtom);
    const amount = get(amountAtom);

    const productType = 'spot' as const;
    const side: 'buy' | 'sell' = sideType === 'Buy' ? 'buy' : 'sell';
    const orderType: 'market_order' | 'trigger_price_order' =
      priceType === 'market' ? 'market_order' : 'trigger_price_order';
    const positionMode = 'oneway' as const;

    return {
      cpe_name: 'order_submitted_latency',
      domain: 'trade',
      log_version: 1,
      symbol,
      side,
      order_type: orderType,
      product_type: productType,
      position_mode: positionMode,
      leverage: 1,
      amount_usdt: amount ? Number(amount) : 0,
      amount: map(quantity, Number) ?? null,
    };
  });

  const onOrderSuccess = (ctx: OrderEventContext) => {
    ctx.mark('postApiEndTime');
    ctx.markApiFinish();
  };

  const onOrderFailed = (ctx: OrderEventContext, errorInfo: OrderEventErrorInfo) => {
    ctx.markError(errorInfo);
    ctx.mark('postApiEndTime');
    ctx.markApiFinish();
  };

  return {
    orderEventContextInfoAtom,
    trackOrderClick,
    onOrderSuccess,
    onOrderFailed,
  };
});
spotEventBunja.debugLabel = 'spotEventBunja';

const subscribeOrderEventBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const { spotOrdersAtom } = bunja.use(spotOrdersBunja);

  const matchOrder = (slot: string) => {
    const orders = store.get(spotOrdersAtom);
    return orders?.[slot];
  };

  const addOrderMessage = (order: SpotOrder | undefined, slot: string, type: 'insert' | 'update' | 'delete') => {
    const ctx = map(order, (order) => OrderEventTracker.instance.getContext(order.orderId));
    if (!ctx || ctx.type !== type) {
      OrderEventTracker.instance.addSpotOrderMessage((context) => {
        const matchedOrder = order ?? matchOrder(slot);
        if (!matchedOrder) return false;
        return (
          context.info.product_type === 'spot' && context.type === type && matchedOrder.orderId === context.requestId
        );
      });
      return;
    }
    ctx.markSocketFinish();
  };

  bunja.effect(() => {
    return r230522Socket.on('private/spot-orders', (event) => {
      const insertEvents = Object.entries(event.data?.i ?? {});
      const updateEventSlots = Object.keys(event.data?.u ?? {});
      const deleteEventSlots = Object.keys(event.data?.d ?? {});

      insertEvents.forEach(([slot, order]) => {
        addOrderMessage(order, slot, 'insert');
      });

      updateEventSlots.forEach((slot) => {
        addOrderMessage(matchOrder(slot), slot, 'update');
      });

      deleteEventSlots.forEach((slot) => {
        const order = matchOrder(slot);
        if (!order) return;

        addOrderMessage(matchOrder(slot), slot, 'delete');
      });
    });
  });
});
subscribeOrderEventBunja.debugLabel = 'subscribeOrderEventBunja';
